import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Shield,
  LogOut,
  Search,
  Building2,
  Users,
  Calendar,
  TrendingUp,
  ChevronRight,
  Filter,
  RefreshCw,
  Eye,
  UserPlus,
  DollarSign
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [totalBusinesses, setTotalBusinesses] = useState(0);

  const getAuthHeader = () => {
    const token = localStorage.getItem('slotmate_admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      }
    }
  }, []);

  const fetchBusinesses = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/admin/businesses?${params}`, {
        headers: getAuthHeader()
      });
      setBusinesses(response.data.businesses);
      setTotalBusinesses(response.data.total);
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
    }
  }, [searchQuery, statusFilter]);

  useEffect(() => {
    const token = localStorage.getItem('slotmate_admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchBusinesses()]);
      setLoading(false);
    };
    loadData();
  }, [navigate, fetchStats, fetchBusinesses]);

  const handleLogout = () => {
    localStorage.removeItem('slotmate_admin_token');
    navigate('/admin');
  };

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([fetchStats(), fetchBusinesses()]);
    setLoading(false);
    toast.success('Data refreshed');
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      trial: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      expired: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return variants[status] || variants.trial;
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white/60 animate-pulse">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="bg-white/5 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">SlotMate Admin</h1>
              <p className="text-white/40 text-xs">Platform Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              className="text-white/60 hover:text-white hover:bg-white/10"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white/60 hover:text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Pending Registrations Banner */}
        {stats?.pending_registrations > 0 && (
          <div
            className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 flex items-center justify-between cursor-pointer hover:bg-amber-500/15 transition-colors"
            onClick={() => navigate('/admin/registration-requests')}
            data-testid="pending-registrations-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-amber-300 font-medium">
                  {stats.pending_registrations} pending registration{stats.pending_registrations > 1 ? 's' : ''}
                </p>
                <p className="text-amber-400/60 text-sm">Click to review and approve business registrations</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-amber-400/60" />
          </div>
        )}

        {/* Registration Requests Quick Link */}
        <div className="flex justify-end mb-4 gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/plans')}
            className="text-white/60 hover:text-white hover:bg-white/10"
            data-testid="manage-plans-btn"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Manage Plans
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/registration-requests')}
            className="text-white/60 hover:text-white hover:bg-white/10"
            data-testid="view-registrations-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Registration Requests
          </Button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Building2}
            label="Total Businesses"
            value={stats?.businesses?.total || 0}
            subValue={`+${stats?.businesses?.new_last_30_days || 0} this month`}
            color="purple"
          />
          <StatCard
            icon={TrendingUp}
            label="Active Subscriptions"
            value={stats?.businesses?.active || 0}
            subValue={`${stats?.businesses?.trial || 0} in trial`}
            color="emerald"
          />
          <StatCard
            icon={Users}
            label="Total Users"
            value={(stats?.users?.total_owners || 0) + (stats?.users?.total_staff || 0)}
            subValue={`${stats?.users?.total_customers || 0} customers`}
            color="blue"
          />
          <StatCard
            icon={Calendar}
            label="Total Appointments"
            value={stats?.appointments?.total || 0}
            subValue="All time"
            color="amber"
          />
        </div>

        {/* Subscription Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-sm">Active</span>
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                {stats?.businesses?.active || 0}
              </Badge>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${stats?.businesses?.total ? (stats.businesses.active / stats.businesses.total) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-sm">Trial</span>
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                {stats?.businesses?.trial || 0}
              </Badge>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-amber-500 rounded-full transition-all"
                style={{ width: `${stats?.businesses?.total ? (stats.businesses.trial / stats.businesses.total) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-sm">Expired</span>
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                {stats?.businesses?.expired || 0}
              </Badge>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-red-500 rounded-full transition-all"
                style={{ width: `${stats?.businesses?.total ? (stats.businesses.expired / stats.businesses.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>

        {/* Businesses Section */}
        <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <h2 className="text-white font-semibold flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Businesses ({totalBusinesses})
              </h2>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <Input
                    placeholder="Search businesses..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-white/40 w-full sm:w-64"
                    data-testid="search-input"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="status-filter"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="trial">Trial</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>
          </div>

          {/* Businesses List */}
          <div className="divide-y divide-white/10">
            {businesses.length === 0 ? (
              <div className="p-8 text-center text-white/40">
                No businesses found
              </div>
            ) : (
              businesses.map((business) => (
                <div
                  key={business.id}
                  className="p-4 hover:bg-white/5 transition-colors cursor-pointer group"
                  onClick={() => navigate(`/admin/businesses/${business.id}`)}
                  data-testid={`business-row-${business.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-white font-medium truncate">{business.name}</h3>
                        <Badge className={getStatusBadge(business.subscription_status || 'trial')}>
                          {business.subscription_status || 'trial'}
                        </Badge>
                      </div>
                      <p className="text-white/40 text-sm truncate">
                        {business.email} • Owner: {business.owner?.name || business.owner?.email || 'Unknown'}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-white/30">
                        <span>{business.stats?.total_appointments || 0} appointments</span>
                        <span>{business.stats?.total_staff || 0} staff</span>
                        <span>{business.stats?.total_services || 0} services</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white/40 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-white/40 transition-colors" />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, subValue, color }) => {
  const colors = {
    purple: 'from-purple-500 to-indigo-600',
    emerald: 'from-emerald-500 to-teal-600',
    blue: 'from-blue-500 to-cyan-600',
    amber: 'from-amber-500 to-orange-600'
  };

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl p-5 border border-white/10">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colors[color]} flex items-center justify-center shadow-lg`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <div className="text-3xl font-bold text-white mb-1">{value.toLocaleString()}</div>
      <div className="text-sm text-white/40">{label}</div>
      <div className="text-xs text-white/30 mt-1">{subValue}</div>
    </div>
  );
};

export default AdminDashboard;